import { ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, LogOut, Shield, ChevronDown, Bell, BarChart3 } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store';
import Logo from '../shared/Logo';
import ThemeToggle from '../shared/ThemeToggle';
import { APP_VERSION } from '../../utils/constants';

interface AppShellProps {
  children: ReactNode;
}

export default function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };
  
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  
  // Calculate trial days remaining
  const trialDaysRemaining = user?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(user.trialEndsAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  
  return (
    <div className="h-screen flex flex-col bg-page overflow-hidden">
      {/* Trial Banner */}
      {user?.status === 'trial' && trialDaysRemaining !== null && trialDaysRemaining <= 3 && (
        <div 
          className="px-4 py-2 text-center text-sm font-medium"
          style={{
            backgroundColor: 'var(--color-warning-bg)',
            color: 'var(--color-warning)',
            borderBottom: '1px solid var(--color-warning)',
          }}
        >
          {trialDaysRemaining === 0 ? (
            'Dein Trial endet heute!'
          ) : trialDaysRemaining === 1 ? (
            'Dein Trial endet morgen.'
          ) : (
            `Dein Trial endet in ${trialDaysRemaining} Tagen.`
          )}
        </div>
      )}
      
      {/* Header */}
      <header 
        className="h-14 flex items-center justify-between px-4"
        style={{
          backgroundColor: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-default)',
        }}
      >
        <Link to="/dashboard" className="flex items-center gap-2">
          <Logo variant="icon" className="h-7 w-7" />
          <span 
            className="text-lg font-bold hidden sm:block"
            style={{ color: 'var(--text-primary)' }}
          >
            kolumi
          </span>
          <span 
            className="text-[10px] hidden sm:block"
            style={{ color: 'var(--text-disabled)' }}
          >
            v{APP_VERSION}
          </span>
        </Link>
        
        <div className="flex items-center gap-1">
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Notifications placeholder */}
          <button 
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <Bell className="w-5 h-5" />
          </button>
          
          {/* User Menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-2 rounded-lg transition-colors"
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-violet-500 flex items-center justify-center text-white text-sm font-medium">
                {user?.displayName?.charAt(0).toUpperCase() || 'U'}
              </div>
              <span 
                className="text-sm hidden sm:block"
                style={{ color: 'var(--text-secondary)' }}
              >
                {user?.displayName}
              </span>
              <ChevronDown className="w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            </button>
            
            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowUserMenu(false)}
                />
                <div 
                  className="absolute right-0 top-full mt-1 w-48 rounded-lg shadow-lg z-20 overflow-hidden"
                  style={{
                    backgroundColor: 'var(--bg-primary)',
                    border: '1px solid var(--border-default)',
                  }}
                >
                  <div 
                    className="p-3"
                    style={{ borderBottom: '1px solid var(--border-default)' }}
                  >
                    <p 
                      className="text-sm font-medium truncate"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {user?.displayName}
                    </p>
                    <p 
                      className="text-xs truncate"
                      style={{ color: 'var(--text-tertiary)' }}
                    >
                      {user?.email}
                    </p>
                  </div>
                  
                  <div className="py-1">
                    <Link
                      to="/analytics"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <BarChart3 className="w-4 h-4" />
                      Analytics
                    </Link>
                    
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm transition-colors"
                      style={{ color: 'var(--text-secondary)' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <Settings className="w-4 h-4" />
                      Einstellungen
                    </Link>
                    
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm transition-colors"
                        style={{ color: 'var(--text-secondary)' }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <Shield className="w-4 h-4" />
                        Admin
                      </Link>
                    )}
                  </div>
                  
                  <div 
                    className="py-1"
                    style={{ borderTop: '1px solid var(--border-default)' }}
                  >
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-3 py-2 text-sm transition-colors w-full text-left"
                      style={{ color: 'var(--color-error)' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-hover)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <LogOut className="w-4 h-4" />
                      Abmelden
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {children}
      </main>
    </div>
  );
}
